import React from 'react';
import { FEED_CARD_SHELL, FEED_INSPECT_BTN, FEED_TITLE } from './investigation/feedCardStyles';
import MatchBadge from './investigation/MatchBadge';

/**
 * Archived Evidence card for Wayback Machine SSE rows (``ui_bucket: wayback_archive``).
 *
 * @param {{ data: object, idx?: string|number, openLabel?: string }} props
 */
export default function WaybackTimelineCard({ data, idx, openLabel }) {
  if (!data || data.ui_bucket !== 'wayback_archive') return null;

  const firstSeen = data.first_seen || null;
  const lastSeen = data.last_seen || null;
  const count = Number(data.snapshot_count) || 0;
  const targetUrl = data.target_url || '';
  const waybackUrl = data.archive_url || data.wayback_url || data.url || '';
  const noSnapshots = count === 0 || data.status === 'no_snapshots';
  const isGaia =
    data.is_gaia_archive === true ||
    Boolean(data.gaia_id) ||
    String(targetUrl || waybackUrl).toLowerCase().includes('plus.google.com');
  const titleHost = (() => {
    if (isGaia) {
      return data.title && String(data.title).toLowerCase().includes('google')
        ? data.title
        : 'Google Plus Historical Archive';
    }
    try {
      if (targetUrl) return new URL(targetUrl).hostname || targetUrl;
    } catch {
      /* ignore */
    }
    return data.title || targetUrl || 'Archived URL';
  })();

  const confidencePct = noSnapshots ? 55 : Math.min(90, 55 + Math.min(count, 10) * 3);
  const cta =
    openLabel ||
    (noSnapshots
      ? 'Open Archive'
      : count > 0
        ? `Open Archive (${count} captures)`
        : 'Open Archive');

  return (
    <article key={idx} className={FEED_CARD_SHELL} data-testid="wayback-timeline-card">
      <section className="flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:items-center sm:gap-6">
        <section className="flex min-w-0 flex-1 items-center gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-950/40 text-2xl shadow-inner">
            🗄️
          </span>
          <span className="min-w-0 truncate">
            <h4 className="text-[11px] font-black uppercase tracking-[0.12em] text-amber-200 sm:text-xs sm:tracking-[0.14em]">
              Wayback Machine
            </h4>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2">
              <p className={`truncate ${FEED_TITLE}`}>{titleHost}</p>
              <MatchBadge value={confidencePct} />
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <span className="inline-block rounded-full border border-amber-500/40 bg-amber-950/50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-100 sm:text-[11px]">
                {isGaia ? 'Google+ Historical Archive' : 'Archived Evidence (Wayback Machine)'}
              </span>
              {noSnapshots ? (
                <span className="inline-block rounded-full border border-slate-500/40 bg-slate-900/60 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-slate-200 sm:text-[11px]">
                  Status: No Snapshots Recorded
                </span>
              ) : null}
            </div>
          </span>
        </section>

        <section className="flex w-full flex-shrink-0 flex-col items-stretch gap-2 sm:w-auto sm:items-end">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 font-mono sm:pb-0">
            <span className="flex min-w-[100px] flex-col items-center justify-center rounded-xl border border-white/10 bg-slate-950/80 px-4 py-2.5 text-slate-200">
              <span className="mb-0.5 block text-[10px] font-black uppercase tracking-wider text-slate-400 sm:text-[11px]">
                First Indexed
              </span>
              <span className="text-[11px] font-black sm:text-xs">{firstSeen || '—'}</span>
            </span>
            <span className="flex min-w-[100px] flex-col items-center justify-center rounded-xl border border-white/10 bg-slate-950/80 px-4 py-2.5 text-slate-200">
              <span className="mb-0.5 block text-[10px] font-black uppercase tracking-wider text-slate-400 sm:text-[11px]">
                Latest Snapshot
              </span>
              <span className="text-[11px] font-black sm:text-xs">{lastSeen || '—'}</span>
            </span>
            {count > 0 && (
              <span className="flex min-w-[72px] flex-col items-center justify-center rounded-xl border border-amber-500/30 bg-amber-950/40 px-3 py-2.5 text-amber-100">
                <span className="mb-0.5 block text-[10px] font-black uppercase tracking-wider text-amber-200/90 sm:text-[11px]">
                  Captures
                </span>
                <span className="text-[11px] font-black sm:text-xs">{count}</span>
              </span>
            )}
          </div>
          {waybackUrl ? (
            <a
              href={waybackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={FEED_INSPECT_BTN}
              data-testid="wayback-check-archive"
            >
              {cta}
            </a>
          ) : null}
        </section>
      </section>
    </article>
  );
}
